<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="card">
      <div class="card-body">

        <!-- ACTION BAR -->
        <div class="row mb-2">
          <div class="col-md-6">
            <div class="card-title">Semua Video KMS</div>
          </div>
          <div class="col-md-6 text-end">
            <button class="btn btn-success btn-sm" @click="openAdd">
              <i class="mdi mdi-plus"></i> Tambah Video
            </button>
          </div>
        </div>

        <hr />

        <!-- FILTER -->
        <div class="row mb-3">
          <div class="col-md-3">
            <label>Status</label>
            <select
              class="form-select"
              v-model="filterStatus"
              @change="applyFilter"
            >
              <option value="">ALL</option>
              <option value="ENABLE">ENABLE</option>
              <option value="DISABLE">DISABLE</option>
            </select>
          </div>

          <div class="col-md-3">
            <label>Kategori</label>
            <select
              class="form-select"
              v-model="filterCategory"
              @change="applyFilter"
            >
              <option value="">ALL</option>
              <option
                v-for="c in categories"
                :key="c.id"
                :value="c.id"
              >
                {{ c.name }}
              </option>
            </select>
          </div>

          <div class="col-md-4 offset-md-2">
            <label>Cari</label>
            <input
              type="text"
              class="form-control"
              placeholder="Cari judul video..."
              v-model="search"
              @input="searchDatatable"
            />
          </div>
        </div>

        <!-- TABLE -->
        <datatable
          :columns="columns"
          :sortKey="sortKey"
          :sortOrders="sortOrders"
          @sort="sortBy"
        >
          <tbody>
            <tr v-if="loadingTable">
              <td colspan="6" class="text-center">
                <i class="fas fa-spinner fa-spin"></i> Loading...
              </td>
            </tr>

            <tr v-else-if="table_data.length === 0">
              <td colspan="6" class="text-center text-muted">
                Data video belum tersedia
              </td>
            </tr>

            <tr
              v-else
              v-for="(row, i) in table_data"
              :key="row.id"
            >
              <td class="text-center">{{ i + 1 }}</td>
              <td>{{ row.title }}</td>
              <td>{{ row.category_name || "-" }}</td>

              <td class="text-center">
                <span
                  class="badge badge-pill font-size-12"
                  :class="
                    row.is_active
                      ? 'badge-soft-success'
                      : 'badge-soft-danger'
                  "
                >
                  {{ row.is_active ? "ENABLE" : "DISABLE" }}
                </span>
              </td>

              <td class="text-center">
                <div class="btn-group">
                  <button
                    class="btn btn-primary btn-sm"
                    @click="openEdit(row.id)"
                  >
                    <i class="bx bx-edit-alt"></i> Edit
                  </button>

                  <button
                    class="btn btn-sm"
                    :class="
                      row.is_active ? 'btn-danger' : 'btn-success'
                    "
                    @click="toggleStatus(row)"
                  >
                    {{ row.is_active ? "Disable" : "Enable" }}
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </datatable>
      </div>
    </div>

    <!-- MODAL -->
    <b-modal
      v-model="showModal"
      :title="modalTitle"
      hide-footer
      size="lg"
    >
      <FormVideo
        v-if="showModal"
        :id="selectedId"
        @success="onSuccess"
        @cancel="showModal = false"
      />
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "@/router/layouts/main";
import PageHeader from "@/components/page-header";
import Datatable from "@/router/layouts/table-datatable.vue";
import FormVideo from "./FormVideo.vue";
import { debounce } from "@/helpers/debounce";
import axios from "axios";
import Swal from "sweetalert2";

export default {
  components: {
    Layout,
    PageHeader,
    Datatable,
    FormVideo,
  },

  data() {
    let sortOrders = {};
    const columns = [
      { width: "60px", label: "No", name: "-" },
      { label: "Judul Video", name: "title" },
      { label: "Kategori", name: "-" },
      { width: "120px", label: "Status", name: "-" },
      { width: "180px", label: "Aksi", name: "-" },
    ];
    columns.forEach(c => (sortOrders[c.name] = -1));

    return {
      title: "Video KMS",
      items: [
        { text: "KMS", href: "/" },
        { text: "Video", active: true },
      ],

      loadingTable: true,
      raw_data: [],
      table_data: [],

      columns,
      sortKey: "title",
      sortOrders,

      search: "",
      filterStatus: "",
      filterCategory: "",

      categories: [],

      showModal: false,
      selectedId: null,
      modalTitle: "",
    };
  },

  created() {
    this.searchDatatable = debounce(this.applyFilter, 400);
  },

  mounted() {
    this.getDataTable();
    this.fetchCategories();
  },

  methods: {
    getAuthHeader() {
      return {
        Authorization: "Bearer " + localStorage.access_token,
      };
    },

    getDataTable() {
      this.loadingTable = true;
      axios
        .get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master/kms/video",
          { headers: this.getAuthHeader() }
        )
        .then(res => {
          this.raw_data = res.data.data || [];
          this.applyFilter();
        })
        .finally(() => (this.loadingTable = false));
    },

    fetchCategories() {
      axios
        .get(
          process.env.VUE_APP_BACKEND_URL_VERSION +
            "master/kms/video/kategori",
          { headers: this.getAuthHeader() }
        )
        .then(res => {
          this.categories = res.data.data || [];
        });
    },

    applyFilter() {
      let data = [...this.raw_data];

      // FILTER STATUS
      if (this.filterStatus === "ENABLE") {
        data = data.filter(d => d.is_active);
      } else if (this.filterStatus === "DISABLE") {
        data = data.filter(d => !d.is_active);
      }

      // FILTER KATEGORI
      if (this.filterCategory) {
        data = data.filter(
          d => d.category_id === this.filterCategory
        );
      }

      // SEARCH
      if (this.search) {
        const q = this.search.toLowerCase();
        data = data.filter(d =>
          d.title.toLowerCase().includes(q)
        );
      }

      this.table_data = data;
    },

    sortBy(key) {
      if (key === "-") return;
      this.sortOrders[key] *= -1;
      this.table_data.sort((a, b) =>
        a[key] > b[key]
          ? this.sortOrders[key]
          : -this.sortOrders[key]
      );
    },

    openAdd() {
      this.selectedId = null;
      this.modalTitle = "Tambah Video";
      this.showModal = true;
    },

    openEdit(id) {
      this.selectedId = id;
      this.modalTitle = "Edit Video";
      this.showModal = true;
    },

    onSuccess() {
      this.showModal = false;
      this.getDataTable();
    },

    toggleStatus(row) {
      Swal.fire({
        title: "Konfirmasi",
        text: row.is_active
          ? "Nonaktifkan video?"
          : "Aktifkan video?",
        showCancelButton: true,
        confirmButtonText: "Ya",
      }).then(r => {
        if (!r.isConfirmed) return;

        axios
          .patch(
            process.env.VUE_APP_BACKEND_URL_VERSION +
              `master/kms/video/${row.id}/toggle-status`,
            {},
            { headers: this.getAuthHeader() }
          )
          .then(() => this.getDataTable());
      });
    },
  },
};
</script>
